import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { apiFetch, resetApiToken } from '@/domains/general/api/client';

import { getActiveBackend, getBackend } from './authBackend';
import { initialsFromName } from './initials';
import { classifyMeFailure } from './meFailure';
import { type RefreshOutcome, switchToPerspective } from './perspectiveSwitch';
import {
  adoptActivePerspective,
  clearSelectedPerspective,
  getSelectedPerspective,
  onSelectedPerspectiveChanged,
} from './selectedPerspective';
import {
  clearSelectedTenant,
  getSelectedTenant,
  onSelectedTenantCleared,
  setSelectedTenant,
} from './selectedTenant';
import type { TenantCode } from './tenants';
import type { AuthContextValue, User, UserPerspective } from './types';

type MeResponse = {
  id: string;
  name: string;
  tenantId: string;
  isAdmin: boolean;
  activePerspective: UserPerspective | null;
  perspectives: UserPerspective[];
};

type EnrichResult = { status: 'ok'; user: User } | { status: 'session-expired' };

async function enrichWithMe(user: User): Promise<EnrichResult> {
  // De keuze waarmee deze /me verstuurd wordt (client.ts leest dezelfde opslag).
  const requested = getSelectedPerspective();
  try {
    const me = await apiFetch<MeResponse>('/me');
    // De API valt terug op het standaardperspectief bij een onbekende keuze;
    // door het actieve perspectief over te nemen loopt de opgeslagen keuze daar
    // weer mee gelijk, tenzij er intussen een nieuwere keuze is gemaakt.
    if (me.activePerspective) adoptActivePerspective(requested, me.activePerspective.id);
    return {
      status: 'ok',
      user: {
        ...user,
        name: me.name,
        initials: initialsFromName(me.name),
        activePerspective: me.activePerspective,
        perspectives: me.perspectives,
      },
    };
  } catch (error) {
    if (classifyMeFailure(error) === 'session-expired') {
      return { status: 'session-expired' };
    }
    // Geen perspectief (403) of /me onbereikbaar: val terug op de naam uit de
    // auth-backend zonder perspectief.
    return { status: 'ok', user: { ...user, activePerspective: null, perspectives: [] } };
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Alles wat alleen in dit tabblad leeft. Querysleutels bevatten geen tenant,
  // gebruiker of perspectief: zonder wissen serveert React Query de data van de
  // vorige sessie. De perspectiefkeuze hoort bij de sessie en gaat mee.
  const clearLocalSession = useCallback(() => {
    resetApiToken();
    clearSelectedPerspective();
    queryClient.clear();
    setUser(null);
  }, [queryClient]);

  const endSession = useCallback(() => {
    // Sessie is verlopen: terug naar het loginscherm zodat de gebruiker
    // opnieuw zijn zone kan kiezen. Geen MSAL-logoutPopup — die wordt zonder
    // gebruikersactie door de browser geblokkeerd.
    clearSelectedTenant();
    clearLocalSession();
    setSessionExpired(true);
  }, [clearLocalSession]);

  // Tenantkeuze en tokencache staan in localStorage en zijn dus gedeeld, maar
  // de React-state hieronder niet: zonder deze listener blijft dit tabblad na
  // uitloggen elders zijn ingelogde scherm tonen tot een API-call 401 geeft.
  useEffect(
    () =>
      onSelectedTenantCleared(() => {
        clearLocalSession();
        // Geen "sessie verlopen"-melding: de gebruiker heeft zelf uitgelogd, en
        // de tab waar dat gebeurde toont die melding al als het wél een
        // verlopen sessie was.
        setSessionExpired(false);
      }),
    [clearLocalSession],
  );

  useEffect(() => {
    let cancelled = false;
    // Geen opgeslagen tenantkeuze = geen actieve sessie (keuze wordt bij
    // logout gewist; bij een nieuwe sessie kiest de gebruiker opnieuw).
    const code = getSelectedTenant();
    if (!code) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    getBackend(code)
      .restoreSession()
      .then(async (restored) => {
        if (cancelled) return;
        if (!restored) {
          setUser(null);
          return;
        }
        const result = await enrichWithMe(restored);
        if (cancelled) return;
        if (result.status === 'session-expired') {
          endSession();
          return;
        }
        setUser(result.user);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endSession]);

  // /me opnieuw ophalen voor de huidige gebruiker, bv. na een perspectiefwissel.
  const refreshUser = useCallback(async (): Promise<RefreshOutcome> => {
    const backendUser = getActiveBackend()?.getUser();
    if (!backendUser) return 'ignored';
    const result = await enrichWithMe(backendUser);
    if (result.status === 'session-expired') {
      endSession();
      return 'ignored';
    }
    const activeId = result.user.activePerspective?.id;
    // Zonder perspectief is /me niet goed doorgekomen: de huidige gebruiker
    // laten staan in plaats van het "geen toegang"-scherm te tonen.
    if (activeId === undefined) return 'failed';
    // Intussen een nieuwere keuze gemaakt: dit antwoord hoort bij een oude header.
    if (getSelectedPerspective() !== activeId) return 'ignored';
    setUser(result.user);
    return 'updated';
  }, [endSession]);

  const switchPerspective = useCallback(
    async (perspectiveId: string) => {
      try {
        await switchToPerspective(perspectiveId, {
          resetQueries: () => queryClient.resetQueries(),
          refreshUser,
        });
      } catch (error) {
        console.warn('[authProvider] perspectief wisselen mislukt', error);
      }
    },
    [queryClient, refreshUser],
  );

  // Wisselt een ander tabblad van perspectief, dan gaan de requests van dit
  // tabblad al met de nieuwe header (gedeelde localStorage). Zonder dit toont
  // het nog de oude badge en data. Alleen als dit tabblad zelf een gebruiker
  // met perspectief toont en de keuze echt afwijkt: een tab die alleen een
  // terugval corrigeert levert anders een nutteloze reset op, en een
  // uitgelogde tab mag via dit signaal niet inloggen of een sessie beëindigen.
  const activePerspectiveId = user?.activePerspective?.id ?? null;
  useEffect(
    () =>
      onSelectedPerspectiveChanged((perspectiveId) => {
        if (activePerspectiveId === null || perspectiveId === activePerspectiveId) return;
        void switchPerspective(perspectiveId);
      }),
    [switchPerspective, activePerspectiveId],
  );

  const loginWithSSO = useCallback(
    async (code: TenantCode) => {
      setSelectedTenant(code);
      setSessionExpired(false);
      // Ook bij login opruimen, voor het geval de vorige sessie eindigde zonder
      // nette logout.
      clearLocalSession();
      const next = await getBackend(code).loginWithSSO();
      const result = await enrichWithMe(next);
      if (result.status === 'session-expired') {
        endSession();
        return;
      }
      setUser(result.user);
    },
    [clearLocalSession, endSession],
  );

  const logout = useCallback(async () => {
    await getActiveBackend()?.logout();
    // Tenantkeuze resetten zodat er bij het volgende inloggen opnieuw
    // gekozen wordt. Andere tabs pikken dit op via onSelectedTenantCleared.
    clearSelectedTenant();
    clearLocalSession();
    setSessionExpired(false);
  }, [clearLocalSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      sessionExpired,
      loginWithSSO,
      logout,
      switchPerspective,
    }),
    [user, isLoading, sessionExpired, loginWithSSO, logout, switchPerspective],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
